import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/reduxStore/rootReducer';
import { closeInboundPaymentDetailModal } from '@/reduxStore/slices/ordersSlice';
import { BladeModal } from '@monavate/ui-core';
import styled from 'styled-components';
import { ClickToCopy } from '@monavate/ui-core';
import { KeyValueViewer } from '@monavate/ui-core';
import { getCurrencySign } from '@/helpers/currency';
import { returnCorrectDateAndTimeFormat } from '@/helpers/returnCorrectDateFormat';
import { TwoRowsValues } from '@monavate/ui-core';
import { capitalizeFirstLetter } from '@/helpers/CapitalizeFirstLetter';
import { AmountFormat } from '@monavate/ui-core';
import { CustomerPayment } from '@/api/general';
import { Button } from '@monavate/ui-core';
import { WarningModal } from '@monavate/ui-core';
import { addNotification } from '../../reduxStore/slices/systemNotificationSlice';
import { customerPaymentsApi } from '../../services/rest/apiInstances';
import { fetchOrderInboundPayments } from '../../reduxStore/slices/orderEditFormSlice';
import { useNavigate, useParams } from 'react-router-dom';
import PermissionChecker from '../PermissionChecker/PermissionChecker';
import { UserPermission } from '@/api/client';
import { buildClientApiConfig } from '@/helpers/apiRequestConfig';

const Header = styled.div`
    padding: 32px 32px 24px;
    background: ${(props) => props.theme['Surface/secondary']}
        /* TODO: check color. Alternatives: ["Surface/secondary","Interaction/hover","Interaction/active","Interaction/selected","Interaction/focus","Interaction/disabled"] */;
`;

const RowButtons = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 16px;
    width: 100%;
`;

const BottomEditButton = styled(Button)`
    display: flex;
    height: 40px;
    padding: 8px 16px;
    justify-content: center;
    align-items: center;
    gap: 8px;
    flex: 1 0 0;
    max-width: 50%;
`;

const MainTitle = styled.div`
    color: ${(props) => props.theme['Content/primary']}
        /* TODO: check color. Alternatives: ["Content/primary","Content/logo","Content/on-accent"] */;
    font-family: 'Poppins', serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 24px;
`;

const SecondaryTitle = styled.div`
    color: ${(props) => props.theme['Borders/active']};
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 22px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    padding-right: 20px;
`;

interface BladeModalHeaderProps {
    acquirerReference?: string;
}

const BladeModalHeader = ({ acquirerReference }: BladeModalHeaderProps) => {
    return (
        <Header>
            <MainTitle>Inbound payment</MainTitle>
        </Header>
    );
};

export const createIPDetailsObject = (IP: CustomerPayment) => {
    return [
        {
            name: '',
            rows: [
                {
                    name: 'ID',
                    value: IP?.id && <ClickToCopy dataTestId="value-ip-id">{IP?.id}</ClickToCopy>,
                    showFull: true,
                },
                {
                    name: 'Amount',
                    value: !!IP?.currency && (
                        <ClickToCopy
                            dataTestId="value-ip-amount"
                            value={`${getCurrencySign(IP?.currency)} ${IP?.amount}`}
                        >
                            <AmountFormat
                                amount={IP?.amount || 0}
                                currency={IP?.currency}
                                letters
                            />
                        </ClickToCopy>
                    ),
                },
                {
                    name: 'Payment method',
                    value: IP?.paymentMethod && (
                        <ClickToCopy dataTestId="value-ip-payment-method">
                            {capitalizeFirstLetter(IP?.paymentMethod)}
                        </ClickToCopy>
                    ),
                },
                {
                    name: 'Payment date',
                    value: IP?.date && (
                        <ClickToCopy dataTestId="value-ip-payment-date">
                            {returnCorrectDateAndTimeFormat(IP?.date)}
                        </ClickToCopy>
                    ),
                },
                {
                    name: 'Acquirer reference',
                    value: IP?.acquirerReferenceNumber && (
                        <ClickToCopy dataTestId="value-ip-acquirer-reference">
                            {IP?.acquirerReferenceNumber}
                        </ClickToCopy>
                    ),
                    showFull: true,
                },
                {
                    name: 'Transaction ID',
                    value: IP?.transactionId && (
                        <ClickToCopy dataTestId="value-ip-transaction-id">
                            {IP?.transactionId}
                        </ClickToCopy>
                    ),
                    showFull: true,
                },
                {
                    name: 'Created',
                    value: (
                        <TwoRowsValues
                            topRow={
                                <ClickToCopy dataTestId="value-ip-created-by">
                                    {IP?.createdBy}
                                </ClickToCopy>
                            }
                            bottomRow={
                                <ClickToCopy dataTestId="value-ip-created-at">
                                    {returnCorrectDateAndTimeFormat(IP?.createdAt)}
                                </ClickToCopy>
                            }
                        />
                    ),
                },
                {
                    name: 'Last updated',
                    value: (
                        <TwoRowsValues
                            topRow={
                                <ClickToCopy dataTestId="value-ip-updated-by">
                                    {IP?.updatedBy}
                                </ClickToCopy>
                            }
                            bottomRow={
                                <ClickToCopy dataTestId="value-ip-updated-at">
                                    {returnCorrectDateAndTimeFormat(IP?.updatedAt)}
                                </ClickToCopy>
                            }
                        />
                    ),
                },
            ],
        },
    ];
};

interface InboundPaymentDetailsBladeModalProps {
    isOpen: boolean;
}

const InboundPaymentDetailsBladeModal: React.FC<InboundPaymentDetailsBladeModalProps> = ({
    isOpen,
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { orderReference } = useParams();
    const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);

    const inboundPaymentDetailsToDisplay = useSelector(
        (state: RootState) => state.orders.inboundPaymentDetailsToDisplay,
    );
    const onCloseModalHandler = () => {
        dispatch(closeInboundPaymentDetailModal());
    };

    const onDeleteButtonClickHandler = () => {
        setShowDeleteConfirmModal(true);
    };

    const onEditButtonClickHandler = () => {
        dispatch(closeInboundPaymentDetailModal());
        navigate(`/orders/${orderReference}/${inboundPaymentDetailsToDisplay.id}/update`);
    };

    const onConfirmDelete = async () => {
        try {
            dispatch(closeInboundPaymentDetailModal());
            const options = buildClientApiConfig();
            const deleteRequest = await customerPaymentsApi.cancelCustomerPayment(
                {
                    paymentId: inboundPaymentDetailsToDisplay.id,
                },
                options,
            );
            if (deleteRequest) {
                dispatch(
                    addNotification({
                        message: 'Payment deleted',
                        status: 'success',
                    }),
                );
                if (orderReference) {
                    // @ts-ignore
                    dispatch(fetchOrderInboundPayments({ orderReference }));
                }
            }
        } catch (error: any) {
            const errorMessage = error?.response?.data?.errors?.[0]?.message;
            const differentMessage = error?.response?.data?.message;
            dispatch(
                addNotification({
                    message: errorMessage || differentMessage || 'Something wrong',
                    status: 'failure',
                }),
            );
        }
    };

    return (
        <>
            <WarningModal
                dataTestId="modal-ip-delete-confirm"
                isOpen={showDeleteConfirmModal}
                title="Delete inbound payment"
                content={
                    <span>
                        You’re about to permanently delete{' '}
                        <b>
                            {inboundPaymentDetailsToDisplay?.acquirerReferenceNumber ||
                                'the inbound payment'}
                        </b>
                        . Are you sure you want to proceed with this action?
                    </span>
                }
                okText="Delete"
                okButtonType="destructive-alert"
                cancelText="Cancel"
                onOk={onConfirmDelete}
                onCancel={() => {
                    setShowDeleteConfirmModal(false);
                }}
            />
            <BladeModal
                isOpen={isOpen && inboundPaymentDetailsToDisplay && !showDeleteConfirmModal}
                closeButton={true}
                handleClose={onCloseModalHandler}
                padding={'0 32px'}
                customHeader={
                    <BladeModalHeader
                        acquirerReference={
                            inboundPaymentDetailsToDisplay?.acquirerReferenceNumber ||
                            'Acquirer reference'
                        }
                    />
                }
                customBottomButton={
                    <RowButtons>
                        <PermissionChecker
                            allowedPermissions={[UserPermission.PaymentManager]}
                            VACRequiredPermissions={[UserPermission.ManageClientPayments]}
                        >
                            <Button
                                dataTestId="button-ip-delete"
                                handleClick={onDeleteButtonClickHandler}
                                viewType={'destructive'}
                                style={{ flex: '1 0 0', maxWidth: '50%' }}
                            >
                                <span
                                    style={{
                                        verticalAlign: 'middle',
                                        fontSize: '24px',
                                        marginTop: -2,
                                        marginRight: 8,
                                        fontWeight: 300,
                                    }}
                                    className="material-symbols-sharp"
                                >
                                    delete
                                </span>
                                Delete
                            </Button>{' '}
                            <BottomEditButton
                                dataTestId="button-ip-edit"
                                handleClick={onEditButtonClickHandler}
                            >
                                <span
                                    style={{
                                        verticalAlign: 'middle',
                                        fontSize: '24px',
                                        marginTop: -2,
                                        marginRight: 8,
                                        fontWeight: 300,
                                    }}
                                    className="material-symbols-sharp"
                                >
                                    edit
                                </span>
                                Edit
                            </BottomEditButton>
                        </PermissionChecker>
                    </RowButtons>
                }
            >
                <KeyValueViewer data={createIPDetailsObject(inboundPaymentDetailsToDisplay)} />
            </BladeModal>
        </>
    );
};

export default InboundPaymentDetailsBladeModal;
